import { supabase } from "@/shared/lib/supabaseClient";

/**
 * Convites de ministério (DA-003) — o lado da PESSOA CONVIDADA e a busca de
 * candidatos para convite/cadastro.
 *
 * As três operações vivem em RPCs da migration 0024 (a regra mora no banco):
 *   - aceitar_convite / recusar_convite: só a própria pessoa, só se o
 *     vínculo está 'convidado' (aceitar -> ativo; recusar -> inativo).
 *   - listar_pessoas_para_convite: pessoas ativas ainda sem vínculo
 *     ativo/pendente no ministério — restrita a admin da plataforma ou ao
 *     líder do ministério (fecha a lacuna do líder, que não enxerga a
 *     tabela `pessoas` inteira).
 */

export interface ConviteMinisterio {
  vinculoId: string;
  ministerioId: string;
  ministerioNome: string;
  funcaoNome: string;
}

export interface PessoaParaConvite {
  id: string;
  nome_completo: string;
}

export const convitesService = {
  /** Convites pendentes da pessoa logada (status 'convidado'). */
  async meusConvites(pessoaId: string): Promise<ConviteMinisterio[]> {
    const { data: vinculos, error } = await supabase
      .from("pessoa_ministerio")
      .select("id, ministerio_id, funcao_id")
      .eq("pessoa_id", pessoaId)
      .eq("status", "convidado");
    if (error) throw new Error("Não foi possível carregar seus convites.");
    if (!vinculos?.length) return [];

    const ministerioIds = [...new Set(vinculos.map((v) => v.ministerio_id))];
    const funcaoIds = [...new Set(vinculos.map((v) => v.funcao_id))];
    const [ministeriosRes, funcoesRes] = await Promise.all([
      supabase.from("ministerios").select("id, nome").in("id", ministerioIds),
      supabase.from("funcoes_ministeriais").select("id, nome").in("id", funcaoIds),
    ]);
    if (ministeriosRes.error || funcoesRes.error) {
      throw new Error("Não foi possível carregar os convites.");
    }
    const nomeMinisterio = new Map((ministeriosRes.data ?? []).map((m) => [m.id, m.nome]));
    const nomeFuncao = new Map((funcoesRes.data ?? []).map((f) => [f.id, f.nome]));

    return vinculos.map((v) => ({
      vinculoId: v.id,
      ministerioId: v.ministerio_id,
      ministerioNome: nomeMinisterio.get(v.ministerio_id) ?? "(ministério)",
      funcaoNome: nomeFuncao.get(v.funcao_id) ?? "(função)",
    }));
  },

  async aceitar(vinculoId: string): Promise<void> {
    const { error } = await supabase.rpc("aceitar_convite", { p_vinculo_id: vinculoId });
    if (error) throw new Error("Não foi possível aceitar o convite. Tente novamente.");
  },

  async recusar(vinculoId: string): Promise<void> {
    const { error } = await supabase.rpc("recusar_convite", { p_vinculo_id: vinculoId });
    if (error) throw new Error("Não foi possível recusar o convite. Tente novamente.");
  },

  /**
   * Candidatas a convite/cadastro no ministério (admin ou líder).
   * `funcaoId` (opcional) restringe a exclusão à função específica sendo
   * oferecida — sem isso, quem já é integrante com QUALQUER função some da
   * lista, impedindo dar uma segunda função à mesma pessoa (ex.: já é
   * "Líder", quer também "Gerencia módulo") mesmo o banco permitindo.
   */
  async listarPessoasParaConvite(
    ministerioId: string,
    funcaoId?: string | null,
  ): Promise<PessoaParaConvite[]> {
    const { data, error } = await supabase.rpc("listar_pessoas_para_convite", {
      p_ministerio_id: ministerioId,
      p_funcao_id: funcaoId ?? null,
    });
    if (error) throw new Error("Não foi possível carregar as pessoas disponíveis.");
    return data ?? [];
  },
};
